import './App.scss';
import { Provider } from 'react-redux';
import store from './main/store/store';
import { ThemeProvider } from '@mui/material';
import { theme } from './main/layout/theme';
import checkmateApi, { attachAuthInterceptor } from './main/axios/checkmateApi';
import { addFetch, removeFetch, setToastMessage } from './main/slices/utilSlice';
import ToastMessage from './library/components/ToastMessage/ToastMessage';
import { errorStatusTypeMap } from './library/constants/responseConstants';
import { callLogout } from './main/slices/authSlice';
import React, { useEffect } from 'react';
import { LicenseManager } from  'ag-grid-enterprise'
import AppRouter from './main/routes/AppRouter';
import { assetMap } from './library/util/assetMap';
import { getMsalInstance } from './main/services/msalService';

function App() {

  // Logic to show the loading icon on any http request.
  // placed here so that we can dispatch redux actions without circular dependency import issues.
  checkmateApi.interceptors.request.use(function (config) {
    // Match on the first path segment only — strip leading slashes, then take
    // the portion before the first '/', '?', or '#' — to avoid silencing
    // unrelated endpoints whose URLs merely contain one of these strings as a
    // substring (e.g. 'prompt/history' must NOT be silenced by 'prompt').
    const SILENT_FIRST_SEGMENTS = new Set(['suggest', 'status', 'taxonomy', 'searchtag', 'dashboards']);
    // Multi-segment paths that must be silenced even though their first segment is shared
    // with user-visible endpoints (e.g. search/suggest is typeahead; first segment 'search' is not silent).
    const SILENT_PATHS = ['search/suggest'];
    // Method+path pairs to silence — POST search/result is graph generation, which has its own snackbar indicator.
    const SILENT_METHOD_PATHS: Array<{ method: string; path: string }> = [
      { method: 'post', path: 'search/result' }
    ];
    const pathname = (config.url || '').replace(/^\/+/, '').toLowerCase();
    const method = (config.method || '').toLowerCase();
    const seg = pathname.split(/[/?#]/)[0];
    const isSilent = SILENT_FIRST_SEGMENTS.has(seg)
      || SILENT_PATHS.some(p => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '?'))
      || SILENT_METHOD_PATHS.some(({ method: m, path: p }) => method === m && pathname === p);
    if (!isSilent) {
      store.dispatch(addFetch());
    }
    return config
  }, function (error) {
    return Promise.reject(error);
  });
  // setup general error handler for all API requests.
  checkmateApi.interceptors.response.use(function (response) {
    store.dispatch(removeFetch());
    return response;
  }, function (error) {
    store.dispatch(removeFetch());
    if (!error.response) {
      return Promise.reject(error);
    }
    const statusType = `${error.response.status.toString()[0]}xx`;
    const message = `${error.response.status}: ${error.response.data.responseMessage || error.response.data.message}`;
    store.dispatch(setToastMessage({
      type: errorStatusTypeMap[statusType],
      message
    }));
    if (error.response.status === 401 && (message.includes('expired') || message.includes('token'))) {
      store.dispatch(callLogout());
    }
    return Promise.reject(error);
  });

  useEffect(() => {
    LicenseManager.setLicenseKey('Using_this_{AG_Grid}_Enterprise_key_{AG-067133}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{PSI}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{PSI_Advantage}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{PSI_Advantage}_need_to_be_licensed___{PSI_Advantage}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{17_November_2025}____[v3]_[01]_MTc2MzMzNzYwMDAwMA==c0f32d2af6bde52a45385f04ee7a974f');
  }, []);

  useEffect(() => {
    document.title = assetMap('appTitle');
    const link = document.createElement('link');
    link.rel = "apple-touch-icon shortcut icon";
    link.href = assetMap('titleLogo');
    document.head.appendChild(link);
  }, []);

  // Initialize MSAL on app startup
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await getMsalInstance();
      } catch (error) {
        console.error('Error initializing MSAL on app startup:', error);
      }
    };
    initializeMsal();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <ToastMessage />
        <AppRouter />
      </Provider>
    </ThemeProvider>
  );
}

export default App;
